import { Component, Input, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidePanelService } from '../../../../features/general-agent/services/side-panel.service';
import { AttachmentService } from 'src/app/contract-management/services/attachment.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { MenuComponent, CustomMenuItem } from '../../../../@core/components/ui/menu/menu.component';
import { IconSourceEnum } from '../../../../@core/components/ui/icon/icon.component';
import { ToolCallPanelService } from 'src/app/features/general-agent/services/tool-call-panel.service';
import { getFileTypeLucideIcon } from 'src/app/@shared/utils/utils';

export interface Source {
  source: string;
  resource: string;
  name: string;
  page_number: number;
  confidence: number;
}

@Component({
  selector: 'app-custom-sources',
  standalone: true,
  imports: [CommonModule, MenuComponent],
  templateUrl: './custom-sources.component.html',
  styleUrl: './custom-sources.component.scss'
})
export class CustomSourcesComponent {
  @Input() sources: Source[] = [];
  @ViewChild('sourcesMenu') sourcesMenu!: MenuComponent;

  private toolCallPanelService = inject(ToolCallPanelService);
  private sidePanelService = inject(SidePanelService);
  private attachmentService = inject(AttachmentService);
  private toastService = inject(ToastService);

  get displayText(): string {
    if (!this.sources || this.sources.length === 0) return '';
    if (this.sources.length === 1) {
      return this.sources[0].name || this.sources[0].source || '';
    }
    const firstTitle = this.sources[0].name || this.sources[0].source || '';
    return `${firstTitle} +${this.sources.length - 1}`;
  }

  get sourcesMenuItems(): CustomMenuItem[] {
    if (!this.sources || this.sources.length === 0) return [];

    return this.sources.map((source, index) => {
      const name = source.name || source.source || `Source ${index + 1}`;
      const label = source.page_number
        ? `${name} - Page ${source.page_number}`
        : name;

      return {
        icon: getFileTypeLucideIcon(source.name || source.resource),
        iconSource: IconSourceEnum.LUCIDE,
        label: label,
        value: source,
        callback: () => {
          this.onSourceClick(source);
        },
        class: 'h-5 truncate max-w-full text-blue-600'
      };
    });
  }

  showSourcesPanel(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.sourcesMenu) {
      this.sourcesMenu.toggle(event);
    }
  }

  onSourceClick(source: Source) {
    this.toolCallPanelService.isOpen.set(false);
    if (this.sourcesMenu) {
      this.sourcesMenu.hide();
    }
    const attachmentId = source.source;
    if (!attachmentId) {
      this.toastService.error('Cannot open file: missing attachment id');
      return;
    }
    this.attachmentService.getAttachmentWithSignedUrl(attachmentId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.sidePanelService.open('FILE_PREVIEW', { file: res.data, documentId: res.data._id, source });
        } else {
          this.toastService.error('Failed to load file preview');
        }
      },
      error: (err) => {
        this.toastService.error('Failed to load file preview: ' + (err?.message ?? 'Unknown error'));
      }
    });
  }
}

